<!-- src/components/modal/indicator-modal.vue -->
<template>
  <!-- Add Indicator -->
  <div
    class="modal fade"
    id="add_performance_indicator"
    tabindex="-1"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-dialog-scrollable dq-dialog"
    >
      <div class="modal-content dq-modal-content" :key="addKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-indigo">
              <i class="ti ti-list-check"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Add New Indicator</h4>
              <div class="dq-subtitle">
                Pick scope, approver, and set expected levels.
              </div>
            </div>
          </div>
          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitCreate">
          <div class="modal-body dq-modal-body">
            <div class="dq-info-row">
              <div class="dq-info-pill">
                <i class="ti ti-search"></i>
                Department / Designation / Approver are searchable
              </div>
              <div class="dq-info-pill">
                <i class="ti ti-filter"></i>
                Department filters designations & approvers
              </div>
            </div>

            <!-- Scope card -->
            <div class="dq-card mb-3">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-building"></i> Scope
                </div>
                <div class="dq-card-sub">
                  Choose a department, then designation, then approver within
                  that scope.
                </div>
              </div>

              <div class="row g-3">
                <!-- Department searchable -->
                <div class="col-md-4">
                  <label class="form-label dq-label">Department</label>

                  <div class="dropdown w-100">
                    <button
                      type="button"
                      class="btn btn-white border w-100 text-start dq-dd-btn d-flex align-items-center justify-content-between"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <span class="d-flex align-items-center gap-2">
                        <span class="dq-avatar"
                          ><i class="ti ti-building"></i
                        ></span>
                        <span class="dq-dd-title">{{
                          selectedDepartmentLabel || "Select department"
                        }}</span>
                      </span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div class="dropdown-menu w-100 p-2 dq-dd-menu">
                      <div class="mb-2">
                        <div class="input-icon-start position-relative">
                          <span class="input-icon-addon"
                            ><i class="ti ti-search"></i
                          ></span>
                          <input
                            v-model.trim="deptQuery"
                            class="form-control dq-input"
                            placeholder="Search department…"
                            @keydown.down.prevent="moveDeptActive(1)"
                            @keydown.up.prevent="moveDeptActive(-1)"
                            @keydown.enter.prevent="selectActiveDept()"
                          />
                        </div>
                      </div>

                      <div class="dq-search-list">
                        <button
                          v-for="(opt, idx) in filteredDepartments"
                          :key="'dept-' + opt.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          :class="{ active: idx === deptActiveIndex }"
                          @mouseenter="deptActiveIndex = idx"
                          @click="pickDepartment(opt.id)"
                        >
                          <div class="dq-dd-row">
                            <div class="dq-avatar">
                              <i class="ti ti-building"></i>
                            </div>
                            <div class="flex-grow-1">
                              <div class="dq-dd-title">{{ opt.label }}</div>
                              <div class="dq-dd-sub">Department</div>
                            </div>
                          </div>
                        </button>

                        <div
                          v-if="!filteredDepartments.length"
                          class="px-2 py-3 text-muted small"
                        >
                          No departments found.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-help">
                    Department selection filters designation & approver list.
                  </div>
                </div>

                <!-- Designation searchable (filtered by department) -->
                <div class="col-md-4">
                  <label class="form-label dq-label">Designation</label>

                  <div class="dropdown w-100">
                    <button
                      type="button"
                      class="btn btn-white border w-100 text-start dq-dd-btn d-flex align-items-center justify-content-between"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      :disabled="!form.department_id && !allDesignations.length"
                    >
                      <span class="d-flex align-items-center gap-2">
                        <span class="dq-avatar"
                          ><i class="ti ti-id-badge"></i
                        ></span>
                        <span class="dq-dd-title">
                          {{
                            selectedDesignationLabel ||
                            (form.department_id
                              ? "Select designation"
                              : "Select department first")
                          }}
                        </span>
                      </span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div class="dropdown-menu w-100 p-2 dq-dd-menu">
                      <div class="mb-2">
                        <div class="input-icon-start position-relative">
                          <span class="input-icon-addon"
                            ><i class="ti ti-search"></i
                          ></span>
                          <input
                            v-model.trim="desigQuery"
                            class="form-control dq-input"
                            :placeholder="
                              form.department_id
                                ? 'Search designation…'
                                : 'Pick department first…'
                            "
                            :disabled="!form.department_id"
                            @keydown.down.prevent="moveDesigActive(1)"
                            @keydown.up.prevent="moveDesigActive(-1)"
                            @keydown.enter.prevent="selectActiveDesig()"
                          />
                        </div>
                      </div>

                      <div class="dq-search-list">
                        <button
                          v-for="(opt, idx) in filteredDesignations"
                          :key="'desig-' + opt.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          :class="{ active: idx === desigActiveIndex }"
                          @mouseenter="desigActiveIndex = idx"
                          @click="pickDesignation(opt.id)"
                        >
                          <div class="dq-dd-row">
                            <div class="dq-avatar">
                              <i class="ti ti-id-badge"></i>
                            </div>
                            <div class="flex-grow-1">
                              <div class="dq-dd-title">{{ opt.label }}</div>
                              <div class="dq-dd-sub">Designation</div>
                            </div>
                          </div>
                        </button>

                        <div
                          v-if="!filteredDesignations.length"
                          class="px-2 py-3 text-muted small"
                        >
                          {{
                            form.department_id
                              ? "No designations found."
                              : "Select a department to load designations."
                          }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-help">
                    Only designations in the chosen department appear here.
                  </div>
                </div>

                <!-- Approved By searchable (filtered by dept + designation) -->
                <div class="col-md-4">
                  <label class="form-label dq-label">Approved By</label>

                  <div class="dropdown w-100">
                    <button
                      type="button"
                      class="btn btn-white border w-100 text-start dq-dd-btn d-flex align-items-center justify-content-between"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <span class="d-flex align-items-center gap-2">
                        <span class="dq-avatar"
                          ><i class="ti ti-user-check"></i
                        ></span>
                        <span class="dq-dd-title">{{
                          selectedApproverLabel || "Select approver"
                        }}</span>
                      </span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div class="dropdown-menu w-100 p-2 dq-dd-menu">
                      <div class="mb-2 d-flex gap-2">
                        <div
                          class="flex-grow-1 input-icon-start position-relative"
                        >
                          <span class="input-icon-addon"
                            ><i class="ti ti-search"></i
                          ></span>
                          <input
                            v-model.trim="approverQuery"
                            class="form-control dq-input"
                            placeholder="Search employee…"
                            @keydown.down.prevent="moveApproverActive(1)"
                            @keydown.up.prevent="moveApproverActive(-1)"
                            @keydown.enter.prevent="selectActiveApprover()"
                          />
                        </div>

                        <button
                          type="button"
                          class="btn dq-btn dq-btn-muted"
                          @click="clearApprover()"
                          title="Clear"
                        >
                          <i class="ti ti-x"></i>
                        </button>
                      </div>

                      <div class="dq-search-list">
                        <button
                          v-for="(opt, idx) in filteredApprovers"
                          :key="'appr-' + opt.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          :class="{ active: idx === approverActiveIndex }"
                          @mouseenter="approverActiveIndex = idx"
                          @click="pickApprover(opt.id)"
                        >
                          <div class="dq-dd-row">
                            <div class="dq-avatar">
                              <i class="ti ti-user-check"></i>
                            </div>
                            <div class="flex-grow-1">
                              <div class="dq-dd-title">{{ opt.label }}</div>
                              <div class="dq-dd-sub">
                                {{ opt.departmentLabel || "—" }}
                                <span v-if="opt.designationLabel">
                                  • {{ opt.designationLabel }}</span
                                >
                              </div>
                            </div>
                          </div>
                        </button>

                        <div
                          v-if="!filteredApprovers.length"
                          class="px-2 py-3 text-muted small"
                        >
                          No employees match current filters.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dq-help">
                    Approver list auto-filters by Department, and further by
                    Designation when selected.
                  </div>
                </div>
              </div>
            </div>

            <!-- Technical -->
            <div class="dq-card mb-3">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-device-laptop"></i> Technical
                </div>
                <div class="dq-card-sub">
                  Set expected competency level per technical indicator.
                </div>
              </div>

              <div class="table-responsive dq-tablewrap">
                <table class="table dq-table mb-0">
                  <thead>
                    <tr>
                      <th>Indicator</th>
                      <th style="width: 260px">Expected Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in form.tech" :key="'t-' + i">
                      <td class="fw-semibold">{{ row.indicator }}</td>
                      <td>
                        <vue-select
                          v-model="row.expected_value"
                          :options="[
                            'None',
                            'Beginner',
                            'Intermediate',
                            'Advanced',
                            'Expert / Leader',
                          ]"
                          placeholder="Select Status"
                          class="dq-vselect"
                          :clearable="false"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Organizational -->
            <div class="dq-card mb-3">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-buildings"></i> Organizational
                </div>
                <div class="dq-card-sub">
                  Set expected competency level per organizational indicator.
                </div>
              </div>

              <div class="table-responsive dq-tablewrap">
                <table class="table dq-table mb-0">
                  <thead>
                    <tr>
                      <th>Indicator</th>
                      <th style="width: 260px">Expected Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in form.org" :key="'o-' + i">
                      <td class="fw-semibold">{{ row.indicator }}</td>
                      <td>
                        <vue-select
                          v-model="row.expected_value"
                          :options="[
                            'None',
                            'Beginner',
                            'Intermediate',
                            'Advanced',
                            'Expert / Leader',
                          ]"
                          placeholder="Select Status"
                          class="dq-vselect"
                          :clearable="false"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Status -->
            <div class="dq-card">
              <div class="row g-3 align-items-end">
                <div class="col-md-6">
                  <label class="form-label dq-label">Status</label>
                  <vue-select
                    v-model="form.status"
                    :options="['Active', 'Inactive']"
                    placeholder="Select Status"
                    class="dq-vselect"
                    :clearable="false"
                  />
                </div>

                <div class="col-md-6">
                  <div class="d-flex flex-wrap gap-2 justify-content-md-end">
                    <span
                      v-if="form.status === 'Active'"
                      class="dq-chip dq-chip-ok"
                    >
                      <i class="ti ti-circle-check"></i> Active
                    </span>
                    <span v-else class="dq-chip dq-chip-muted">
                      <i class="ti ti-circle-minus"></i> Inactive
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn dq-btn dq-btn-muted"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="adding"
            >
              <span
                v-if="adding"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Add Indicator
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Edit Indicator -->
  <div
    class="modal fade"
    id="edit_performance_indicator"
    tabindex="-1"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-dialog-scrollable dq-dialog"
    >
      <div class="modal-content dq-modal-content" :key="editKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-amber">
              <i class="ti ti-edit"></i>
            </div>
            <div>
              <h4 class="modal-title dq-title mb-0">Edit Indicator</h4>
              <div class="dq-subtitle">
                Update scope and expected competency levels.
              </div>
            </div>
          </div>
          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitEdit">
          <div class="modal-body dq-modal-body">
            <!-- Scope card -->
            <div class="dq-card mb-3">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-building"></i> Scope
                </div>
                <div class="dq-card-sub">
                  Choose a department, then designation, then approver within
                  that scope.
                </div>
              </div>

              <div class="row g-3">
                <!-- Department searchable -->
                <div class="col-md-4">
                  <label class="form-label dq-label">Department</label>

                  <div class="dropdown w-100">
                    <button
                      type="button"
                      class="btn btn-white border w-100 text-start dq-dd-btn d-flex align-items-center justify-content-between"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <span class="d-flex align-items-center gap-2">
                        <span class="dq-avatar"
                          ><i class="ti ti-building"></i
                        ></span>
                        <span class="dq-dd-title">{{
                          selectedDepartmentLabel || "Select department"
                        }}</span>
                      </span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div class="dropdown-menu w-100 p-2 dq-dd-menu">
                      <div class="mb-2">
                        <div class="input-icon-start position-relative">
                          <span class="input-icon-addon"
                            ><i class="ti ti-search"></i
                          ></span>
                          <input
                            v-model.trim="deptQuery"
                            class="form-control dq-input"
                            placeholder="Search department…"
                            @keydown.down.prevent="moveDeptActive(1)"
                            @keydown.up.prevent="moveDeptActive(-1)"
                            @keydown.enter.prevent="selectActiveDept()"
                          />
                        </div>
                      </div>

                      <div class="dq-search-list">
                        <button
                          v-for="(opt, idx) in filteredDepartments"
                          :key="'dept2-' + opt.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          :class="{ active: idx === deptActiveIndex }"
                          @mouseenter="deptActiveIndex = idx"
                          @click="pickDepartment(opt.id)"
                        >
                          <div class="dq-dd-row">
                            <div class="dq-avatar">
                              <i class="ti ti-building"></i>
                            </div>
                            <div class="flex-grow-1">
                              <div class="dq-dd-title">{{ opt.label }}</div>
                              <div class="dq-dd-sub">Department</div>
                            </div>
                          </div>
                        </button>

                        <div
                          v-if="!filteredDepartments.length"
                          class="px-2 py-3 text-muted small"
                        >
                          No departments found.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Designation searchable (filtered by department) -->
                <div class="col-md-4">
                  <label class="form-label dq-label">Designation</label>

                  <div class="dropdown w-100">
                    <button
                      type="button"
                      class="btn btn-white border w-100 text-start dq-dd-btn d-flex align-items-center justify-content-between"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      :disabled="!form.department_id"
                    >
                      <span class="d-flex align-items-center gap-2">
                        <span class="dq-avatar"
                          ><i class="ti ti-id-badge"></i
                        ></span>
                        <span class="dq-dd-title">
                          {{
                            selectedDesignationLabel ||
                            (form.department_id
                              ? "Select designation"
                              : "Select department first")
                          }}
                        </span>
                      </span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div class="dropdown-menu w-100 p-2 dq-dd-menu">
                      <div class="mb-2">
                        <div class="input-icon-start position-relative">
                          <span class="input-icon-addon"
                            ><i class="ti ti-search"></i
                          ></span>
                          <input
                            v-model.trim="desigQuery"
                            class="form-control dq-input"
                            :placeholder="
                              form.department_id
                                ? 'Search designation…'
                                : 'Pick department first…'
                            "
                            :disabled="!form.department_id"
                            @keydown.down.prevent="moveDesigActive(1)"
                            @keydown.up.prevent="moveDesigActive(-1)"
                            @keydown.enter.prevent="selectActiveDesig()"
                          />
                        </div>
                      </div>

                      <div class="dq-search-list">
                        <button
                          v-for="(opt, idx) in filteredDesignations"
                          :key="'desig2-' + opt.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          :class="{ active: idx === desigActiveIndex }"
                          @mouseenter="desigActiveIndex = idx"
                          @click="pickDesignation(opt.id)"
                        >
                          <div class="dq-dd-row">
                            <div class="dq-avatar">
                              <i class="ti ti-id-badge"></i>
                            </div>
                            <div class="flex-grow-1">
                              <div class="dq-dd-title">{{ opt.label }}</div>
                              <div class="dq-dd-sub">Designation</div>
                            </div>
                          </div>
                        </button>

                        <div
                          v-if="!filteredDesignations.length"
                          class="px-2 py-3 text-muted small"
                        >
                          {{
                            form.department_id
                              ? "No designations found."
                              : "Select a department to load designations."
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Approved By searchable (filtered by dept + designation) -->
                <div class="col-md-4">
                  <label class="form-label dq-label">Approved By</label>

                  <div class="dropdown w-100">
                    <button
                      type="button"
                      class="btn btn-white border w-100 text-start dq-dd-btn d-flex align-items-center justify-content-between"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <span class="d-flex align-items-center gap-2">
                        <span class="dq-avatar"
                          ><i class="ti ti-user-check"></i
                        ></span>
                        <span class="dq-dd-title">{{
                          selectedApproverLabel || "Select approver"
                        }}</span>
                      </span>
                      <i class="ti ti-chevron-down"></i>
                    </button>

                    <div class="dropdown-menu w-100 p-2 dq-dd-menu">
                      <div class="mb-2 d-flex gap-2">
                        <div
                          class="flex-grow-1 input-icon-start position-relative"
                        >
                          <span class="input-icon-addon"
                            ><i class="ti ti-search"></i
                          ></span>
                          <input
                            v-model.trim="approverQuery"
                            class="form-control dq-input"
                            placeholder="Search employee…"
                            @keydown.down.prevent="moveApproverActive(1)"
                            @keydown.up.prevent="moveApproverActive(-1)"
                            @keydown.enter.prevent="selectActiveApprover()"
                          />
                        </div>

                        <button
                          type="button"
                          class="btn dq-btn dq-btn-muted"
                          @click="clearApprover()"
                          title="Clear"
                        >
                          <i class="ti ti-x"></i>
                        </button>
                      </div>

                      <div class="dq-search-list">
                        <button
                          v-for="(opt, idx) in filteredApprovers"
                          :key="'appr2-' + opt.id"
                          type="button"
                          class="dropdown-item dq-dd-item"
                          :class="{ active: idx === approverActiveIndex }"
                          @mouseenter="approverActiveIndex = idx"
                          @click="pickApprover(opt.id)"
                        >
                          <div class="dq-dd-row">
                            <div class="dq-avatar">
                              <i class="ti ti-user-check"></i>
                            </div>
                            <div class="flex-grow-1">
                              <div class="dq-dd-title">{{ opt.label }}</div>
                              <div class="dq-dd-sub">
                                {{ opt.departmentLabel || "—" }}
                                <span v-if="opt.designationLabel">
                                  • {{ opt.designationLabel }}</span
                                >
                              </div>
                            </div>
                          </div>
                        </button>

                        <div
                          v-if="!filteredApprovers.length"
                          class="px-2 py-3 text-muted small"
                        >
                          No employees match current filters.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Technical -->
            <div class="dq-card mb-3">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-device-laptop"></i> Technical
                </div>
              </div>
              <div class="table-responsive dq-tablewrap">
                <table class="table dq-table mb-0">
                  <thead>
                    <tr>
                      <th>Indicator</th>
                      <th style="width: 260px">Expected Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in form.tech" :key="'et-' + i">
                      <td class="fw-semibold">{{ row.indicator }}</td>
                      <td>
                        <vue-select
                          v-model="row.expected_value"
                          :options="[
                            'None',
                            'Beginner',
                            'Intermediate',
                            'Advanced',
                            'Expert / Leader',
                          ]"
                          placeholder="Select Status"
                          class="dq-vselect"
                          :clearable="false"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Organizational -->
            <div class="dq-card mb-3">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-buildings"></i> Organizational
                </div>
              </div>
              <div class="table-responsive dq-tablewrap">
                <table class="table dq-table mb-0">
                  <thead>
                    <tr>
                      <th>Indicator</th>
                      <th style="width: 260px">Expected Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in form.org" :key="'eo-' + i">
                      <td class="fw-semibold">{{ row.indicator }}</td>
                      <td>
                        <vue-select
                          v-model="row.expected_value"
                          :options="[
                            'None',
                            'Beginner',
                            'Intermediate',
                            'Advanced',
                            'Expert / Leader',
                          ]"
                          placeholder="Select Status"
                          class="dq-vselect"
                          :clearable="false"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Status -->
            <div class="dq-card">
              <div class="row g-3 align-items-end">
                <div class="col-md-6">
                  <label class="form-label dq-label">Status</label>
                   <vue-select
                    v-model="form.status"
                    :options="['Active', 'Inactive']"
                    placeholder="Select Status"
                    class="dq-vselect"
                    :clearable="false"
                  />
                </div>
                <div class="col-md-6">
                  <div class="d-flex flex-wrap gap-2 justify-content-md-end">
                    <span
                      v-if="form.status === 'Active'"
                      class="dq-chip dq-chip-ok"
                    >
                      <i class="ti ti-circle-check"></i> Active
                    </span>
                    <span v-else class="dq-chip dq-chip-muted">
                      <i class="ti ti-circle-minus"></i> Inactive
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn dq-btn dq-btn-muted"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="adding"
            >
              <span
                v-if="adding"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              Add Indicator
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal (beautified) -->
  <div class="modal fade" id="delete_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md dq-dialog">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header dq-danger-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red"><i class="ti ti-trash"></i></div>
            <div>
              <h4 class="modal-title dq-title mb-0">Confirm Delete</h4>
              <div class="dq-subtitle">This action cannot be undone.</div>
            </div>
          </div>
          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-danger">
            <i class="ti ti-alert-triangle"></i>
            <div>
              <div class="dq-danger-title">Delete indicator?</div>
              <div class="dq-danger-sub">
                You’re about to permanently remove this indicator.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <button
            type="button"
            class="btn dq-btn dq-btn-muted"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            class="btn btn-danger dq-btn dq-btn-danger"
            @click="
              $emit('saved');
              $emit('deleteConfirmed');
            "
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useIndicatorStore } from "@/stores/indicatorStore";
import { useDepartmentStore } from "@/stores/departmentStore";
import { useDesignationStore } from "@/stores/designationStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import { toast } from "@/utils/toast";

const props = defineProps({ editItem: { type: Object, default: null } });
const emit = defineEmits(["saved", "deleteConfirmed"]);

const store = useIndicatorStore();
const deptStore = useDepartmentStore?.() ?? {
  departments: [],
  fetchDepartments: async () => {},
};
const desigStore = useDesignationStore?.() ?? {
  designations: [],
  fetchDesignations: async () => {},
};
const empStore = useEmployeeStore?.() ?? {
  employees: [],
  fetchEmployees: async () => {},
};

const adding = computed(() => store.loading);
const addKey = ref(0);
const editKey = ref(0);

const LEVELS = [
  "None",
  "Beginner",
  "Intermediate",
  "Advanced",
  "Expert / Leader",
];
const statusOptions = ["Active", "Inactive"];

function defaultTechnical() {
  return [
    { indicator: "Customer Experience", expected_value: "Intermediate" },
    { indicator: "Marketing", expected_value: "Advanced" },
    { indicator: "Management", expected_value: "Advanced" },
    { indicator: "Administration", expected_value: "Advanced" },
    { indicator: "Presentation Skill", expected_value: "Expert / Leader" },
    { indicator: "Quality Of Work", expected_value: "Expert / Leader" },
    { indicator: "Efficiency", expected_value: "Expert / Leader" },
  ];
}
function defaultOrganizational() {
  return [
    { indicator: "Integrity", expected_value: "Advanced" },
    { indicator: "Professionalism", expected_value: "Advanced" },
    { indicator: "Team Work", expected_value: "Advanced" },
    { indicator: "Critical Thinking", expected_value: "Advanced" },
    { indicator: "Conflict Management", expected_value: "Advanced" },
    { indicator: "Attendance", expected_value: "Advanced" },
    { indicator: "Ability To Meet Deadline", expected_value: "Advanced" },
  ];
}

function blankForm() {
  return {
    department_id: "",
    designation_id: "",
    approved_by: "",
    status: "Active",
    tech: defaultTechnical(),
    org: defaultOrganizational(),
  };
}
const form = ref(blankForm());

/** Reset + clear queries & indices */
function resetForm() {
  const blank = blankForm();
  for (const k in blank) form.value[k] = blank[k];
  deptQuery.value = "";
  desigQuery.value = "";
  approverQuery.value = "";
  deptActiveIndex.value = 0;
  desigActiveIndex.value = 0;
  approverActiveIndex.value = 0;
}

/* ---------------- Base options ---------------- */
const departmentOptions = computed(() =>
  (deptStore.departments || []).map((d) => ({
    id: d.id,
    label: d.name ?? d.department_name ?? d.title ?? "-",
  }))
);

const allDesignations = computed(() =>
  (desigStore.designations || []).map((d) => ({
    id: d.id,
    label: d.name ?? d.designation_name ?? d.title ?? "-",
    department_id: d.department_id ?? null,
  }))
);

/**
 * Designations filtered by selected department.
 * ✅ requirement: when choosing department, designation list loads for that department.
 */
const scopedDesignations = computed(() => {
  const depId = form.value.department_id
    ? String(form.value.department_id)
    : "";
  if (!depId) return [];
  return allDesignations.value.filter((x) => String(x.department_id) === depId);
});

/**
 * Employees filtered by:
 * - department (required for filtering)
 * - and designation (if selected)
 *
 * ✅ requirement: when choosing department, also loads employees with those designations.
 */
const allEmployees = computed(() =>
  (empStore.employees || []).map((e) => ({
    id: e.id,
    department_id: e.department_id ?? e?.user?.department_id ?? null,
    designation_id: e.designation_id ?? e?.user?.designation_id ?? null,
    label: `${e?.user?.first_name ?? e.first_name ?? ""} ${
      e?.user?.last_name ?? e.last_name ?? ""
    }`.trim(),
  }))
);

const scopedApprovers = computed(() => {
  const depId = form.value.department_id
    ? String(form.value.department_id)
    : "";
  if (!depId) return allEmployees.value; // allow global if no department selected
  const desigId = form.value.designation_id
    ? String(form.value.designation_id)
    : "";
  return allEmployees.value.filter((e) => {
    const sameDept = String(e.department_id ?? "") === depId;
    if (!sameDept) return false;
    if (!desigId) return true;
    return String(e.designation_id ?? "") === desigId;
  });
});

/* ---------------- Searchable Department ---------------- */
const deptQuery = ref("");
const deptActiveIndex = ref(0);

const filteredDepartments = computed(() => {
  const q = deptQuery.value.trim().toLowerCase();
  if (!q) return departmentOptions.value;
  return departmentOptions.value.filter((x) =>
    (x.label || "").toLowerCase().includes(q)
  );
});

const selectedDepartmentLabel = computed(() => {
  const id = form.value.department_id;
  if (!id) return "";
  return (
    departmentOptions.value.find((x) => String(x.id) === String(id))?.label ||
    ""
  );
});

function moveDeptActive(dir) {
  const len = filteredDepartments.value.length;
  if (!len) return;
  deptActiveIndex.value = (deptActiveIndex.value + dir + len) % len;
  scrollActiveIntoView();
}
function pickDepartment(id) {
  // if department changes, clear dependent selections
  const prev = form.value.department_id ? String(form.value.department_id) : "";
  const next = id ? String(id) : "";
  form.value.department_id = id;

  if (prev !== next) {
    form.value.designation_id = "";
    form.value.approved_by = "";
  }

  deptQuery.value = "";
  deptActiveIndex.value = 0;
  desigQuery.value = "";
  desigActiveIndex.value = 0;
  approverQuery.value = "";
  approverActiveIndex.value = 0;
  document.activeElement?.blur?.();
}
function selectActiveDept() {
  const opt = filteredDepartments.value[deptActiveIndex.value];
  if (opt) pickDepartment(opt.id);
}

/* ---------------- Searchable Designation (scoped) ---------------- */
const desigQuery = ref("");
const desigActiveIndex = ref(0);

const filteredDesignations = computed(() => {
  const base = scopedDesignations.value;
  const q = desigQuery.value.trim().toLowerCase();
  if (!q) return base;
  return base.filter((x) => (x.label || "").toLowerCase().includes(q));
});

const selectedDesignationLabel = computed(() => {
  const id = form.value.designation_id;
  if (!id) return "";
  return (
    scopedDesignations.value.find((x) => String(x.id) === String(id))?.label ||
    ""
  );
});

function moveDesigActive(dir) {
  const len = filteredDesignations.value.length;
  if (!len) return;
  desigActiveIndex.value = (desigActiveIndex.value + dir + len) % len;
  scrollActiveIntoView();
}
function pickDesignation(id) {
  form.value.designation_id = id;
  // changing designation affects approver list
  form.value.approved_by = "";
  desigQuery.value = "";
  desigActiveIndex.value = 0;
  approverQuery.value = "";
  approverActiveIndex.value = 0;
  document.activeElement?.blur?.();
}
function selectActiveDesig() {
  const opt = filteredDesignations.value[desigActiveIndex.value];
  if (opt) pickDesignation(opt.id);
}

/* ---------------- Searchable Approver (scoped) ---------------- */
const approverQuery = ref("");
const approverActiveIndex = ref(0);

const deptLabelById = computed(() => {
  const map = new Map();
  departmentOptions.value.forEach((d) => map.set(String(d.id), d.label));
  return map;
});
const desigLabelById = computed(() => {
  const map = new Map();
  allDesignations.value.forEach((d) => map.set(String(d.id), d.label));
  return map;
});

const approverOptions = computed(() =>
  scopedApprovers.value.map((e) => ({
    id: e.id,
    label: e.label || "-",
    departmentLabel: e.department_id
      ? deptLabelById.value.get(String(e.department_id))
      : "",
    designationLabel: e.designation_id
      ? desigLabelById.value.get(String(e.designation_id))
      : "",
  }))
);

const filteredApprovers = computed(() => {
  const q = approverQuery.value.trim().toLowerCase();
  if (!q) return approverOptions.value;
  return approverOptions.value.filter((x) =>
    (x.label || "").toLowerCase().includes(q)
  );
});

const selectedApproverLabel = computed(() => {
  const id = form.value.approved_by;
  if (!id) return "";
  return (
    approverOptions.value.find((x) => String(x.id) === String(id))?.label || ""
  );
});

function moveApproverActive(dir) {
  const len = filteredApprovers.value.length;
  if (!len) return;
  approverActiveIndex.value = (approverActiveIndex.value + dir + len) % len;
  scrollActiveIntoView();
}
function pickApprover(id) {
  form.value.approved_by = id;
  approverQuery.value = "";
  approverActiveIndex.value = 0;
  document.activeElement?.blur?.();
}
function clearApprover() {
  form.value.approved_by = "";
  approverQuery.value = "";
  approverActiveIndex.value = 0;
}
function selectActiveApprover() {
  const opt = filteredApprovers.value[approverActiveIndex.value];
  if (opt) pickApprover(opt.id);
}

/* --------------- Scroll active dropdown item into view --------------- */
async function scrollActiveIntoView() {
  await nextTick();
  const menus = document.querySelectorAll(".dq-dd-menu .dq-search-list");
  const list = Array.from(menus).find((el) => el.offsetParent !== null);
  if (!list) return;
  const active = list.querySelector(".dropdown-item.active");
  active?.scrollIntoView({ block: "nearest" });
}

/* ---------------- Sync edit item ---------------- */
watch(
  () => props.editItem,
  (val) => {
    if (val) {
      form.value.department_id = val.department_id || "";
      form.value.designation_id = val.designation_id || "";
      form.value.approved_by = val.approved_by || "";
      form.value.status = val.status || "Active";

      const tech = (val.items || [])
        .filter((i) => i.category === "Technical")
        .map((i) => ({
          indicator: i.indicator,
          expected_value: i.expected_value || "",
        }));
      const org = (val.items || [])
        .filter((i) => i.category === "Organizational")
        .map((i) => ({
          indicator: i.indicator,
          expected_value: i.expected_value || "",
        }));

      form.value.tech = tech.length
        ? tech.map((r) => ({ ...r }))
        : defaultTechnical();
      form.value.org = org.length
        ? org.map((r) => ({ ...r }))
        : defaultOrganizational();

      deptQuery.value = "";
      desigQuery.value = "";
      approverQuery.value = "";
      editKey.value++;
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

const isEdit = computed(() => !!props.editItem?.id);

function buildItemsPayload() {
  const items = [];
  (form.value.tech || []).forEach((row) =>
    items.push({
      category: "Technical",
      indicator: row.indicator,
      expected_value: row.expected_value || null,
    })
  );
  (form.value.org || []).forEach((row) =>
    items.push({
      category: "Organizational",
      indicator: row.indicator,
      expected_value: row.expected_value || null,
    })
  );
  return items;
}

async function submitCreate() {
  if (!form.value.department_id) {
    toast("Department is required", "error");
    return;
  }
  const payload = {
    department_id: form.value.department_id || null,
    designation_id: form.value.designation_id || null,
    approved_by: form.value.approved_by || null,
    status: form.value.status || "Active",
    items: buildItemsPayload(),
  };
  await store.create(payload);
  closeNew();
  resetForm();
  addKey.value++;
  emit("saved");
}

async function submitEdit() {
  if (!props.editItem?.id) return;
  if (!form.value.department_id) {
    toast("Department is required", "error");
    return;
  }
  const payload = {
    department_id: form.value.department_id || null,
    designation_id: form.value.designation_id || null,
    approved_by: form.value.approved_by || null,
    status: form.value.status || "Active",
    items: buildItemsPayload(),
  };
  await store.update(props.editItem.id, payload);
  closeEdit();
  resetForm();
  editKey.value++;
  emit("saved");
}

function closeNew() {
  document
    .querySelector('#add_performance_indicator [data-bs-dismiss="modal"]')
    ?.click();
}
function closeEdit() {
  document
    .querySelector('#edit_performance_indicator [data-bs-dismiss="modal"]')
    ?.click();
}

/* ---------------- Lifecycle ---------------- */
let cleanup = null;
onMounted(async () => {
  if (
    !deptStore.departments?.length &&
    typeof deptStore.fetchDepartments === "function"
  )
    await deptStore.fetchDepartments();

  if (
    !desigStore.designations?.length &&
    typeof desigStore.fetchDesignations === "function"
  )
    await desigStore.fetchDesignations();

  if (
    !empStore.employees?.length &&
    typeof empStore.fetchEmployees === "function"
  )
    await empStore.fetchEmployees();

  const addEl = document.getElementById("add_performance_indicator");
  const editEl = document.getElementById("edit_performance_indicator");

  const handleAddShow = () => {
    resetForm();
    addKey.value++;
  };
  const handleAddHidden = () => resetForm();
  const handleEditHidden = () => {
    resetForm();
    editKey.value++;
  };

  addEl?.addEventListener("show.bs.modal", handleAddShow);
  addEl?.addEventListener("hidden.bs.modal", handleAddHidden);
  editEl?.addEventListener("hidden.bs.modal", handleEditHidden);

  cleanup = () => {
    addEl?.removeEventListener("show.bs.modal", handleAddShow);
    addEl?.removeEventListener("hidden.bs.modal", handleAddHidden);
    editEl?.removeEventListener("hidden.bs.modal", handleEditHidden);
  };
});
onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
/* ✅ same “promotion” touch & feel */
.dq-dialog {
  max-width: 980px;
}

/* Shell */
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}
.dq-modal-header {
  padding: 18px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.1), #fff);
}
.dq-danger-head {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #fff);
}
.dq-modal-body {
  padding: 16px 20px;
}

/* Reliable scroll + sticky footer */
.modal-dialog.modal-dialog-scrollable .modal-content {
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.modal-dialog.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
  max-height: calc(92vh - 150px);
  overscroll-behavior: contain;
}
.dq-modal-footer {
  padding: 14px 20px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

/* Header */
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.14);
  color: rgb(79, 70, 229);
}
.dq-hero-amber {
  background: rgba(245, 158, 11, 0.14);
  color: rgb(217, 119, 6);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* Cards */
.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  padding: 14px;
}
.dq-card-head {
  padding: 2px 2px 10px;
}
.dq-card-title {
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dq-card-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Tips */
.dq-info-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.dq-info-pill {
  flex: 1 1 260px;
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: rgba(15, 23, 42, 0.02);
  color: rgba(15, 23, 42, 0.72);
  font-weight: 800;
  font-size: 0.82rem;
}
.dq-info-pill i {
  color: rgba(99, 102, 241, 0.85);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.35);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}
.dq-help {
  margin-top: 6px;
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Dropdown */
.dq-dd-btn {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-dd-menu {
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.16);
}
.dq-search-list {
  max-height: 280px;
  overflow: auto;
  overscroll-behavior: contain;
}
.dq-dd-item {
  border-radius: 12px;
  padding: 10px 10px;
}
.dq-dd-item:hover {
  background: rgba(15, 23, 42, 0.04);
}
.dq-dd-row {
  display: flex;
  gap: 10px;
  align-items: center;
}
.dq-avatar {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.1);
  color: rgb(79, 70, 229);
  display: flex;
  align-items: center;
  justify-content: center;
}
.dq-dd-title {
  font-weight: 900;
}
.dq-dd-sub {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Buttons */
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-muted {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}

/* Chips */
.dq-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 14px;
  font-size: 0.82rem;
  font-weight: 900;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-chip-muted {
  background: rgba(15, 23, 42, 0.04);
  color: rgba(15, 23, 42, 0.7);
}
.dq-chip-ok {
  background: rgba(16, 185, 129, 0.12);
  color: rgb(5, 150, 105);
}

/* Tables */
.dq-tablewrap {
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  overflow: hidden;
}
.dq-table thead th {
  font-size: 0.82rem;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.72);
  background: rgba(15, 23, 42, 0.02);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-table td {
  vertical-align: middle;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

/* Delete warning */
.dq-danger {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.14);
  background: rgba(239, 68, 68, 0.08);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
  margin-top: 2px;
}
.dq-danger-title {
  font-weight: 950;
}
.dq-danger-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.7);
}

@media (max-width: 576px) {
  .dq-dialog {
    max-width: 96vw;
    margin: 0.75rem auto;
  }
}
</style>
